/**
 * Modal Actions Hook
 * 
 * Provides the main modal action functions: openModal, closeModal, closeAllModals.
 * Integrates with data store and URL sync to provide complete modal management.
 */

import * as React from "react";
import type { GlobalModalId } from "../types.js";
import type { ModalDataStoreReturn } from "./useModalDataStore.js";
import type { UrlSyncReturn } from "./useUrlSync.js";

interface ModalActionsConfig {
  modalStack: any; // Mantine modal stack
  dataStore: ModalDataStoreReturn;
  urlSync: UrlSyncReturn;
  defaultReplace: boolean;
}

export interface ModalActionsReturn {
  /** Open a modal with optional data */
  openModal: (id: GlobalModalId, data?: Record<string, any>, options?: { replace?: boolean }) => void;
  /** Close a modal */
  closeModal: (id: GlobalModalId, options?: { replace?: boolean }) => void;
  /** Close all modals */
  closeAllModals: () => void;
}

/**
 * Hook for managing modal actions
 * 
 * Provides the main functions for opening, closing, and managing modals.
 * Integrates with data store and URL sync.
 * 
 * @param config - Configuration with modal stack, data store, and URL sync
 * @returns Object with modal action functions
 * 
 * @example
 * const { openModal, closeModal, closeAllModals } = useModalActions(config);
 * 
 * // Open a modal with data
 * openModal("edit-contact", { contactId: "123" });
 * 
 * // Close a modal
 * closeModal("edit-contact");
 * 
 * // Close all modals
 * closeAllModals();
 */
export const useModalActions = (config: ModalActionsConfig): ModalActionsReturn => {
  // Use refs to store the config to avoid recreating callbacks on every config change
  const configRef = React.useRef(config);
  
  // Update the ref when config changes, but don't trigger callback recreation
  React.useEffect(() => {
    configRef.current = config;
  }, [config]);

  const openModal = React.useCallback(
    (id: GlobalModalId, data?: Record<string, any>, options?: { replace?: boolean }) => {
      const { dataStore, modalStack, urlSync } = configRef.current;
      
      // Re-open cleanly so stack state toggles even when this id was left open
      if (modalStack.state[id]) {
        modalStack.close(id);
      }

      // Store modal data if provided
      if (data) {
        dataStore.setModalData(id, data);
      } else {
        // Debug logging for modals without data
        if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
          console.warn(`⚠️ Opening Modal: ${id} - No data provided`);
        }
      }

      // Open modal in stack
      modalStack.open(id);

      // Sync to URL if enabled
      urlSync.syncModalToUrl(id, options);
    },
    [] // Empty dependency array - callbacks are stable
  );

  const closeModal = React.useCallback(
    (id: GlobalModalId, options?: { replace?: boolean }) => {
      const { dataStore, modalStack, urlSync } = configRef.current;
      
      // Sync to URL first - this will trigger the effect to close the modal
      urlSync.syncModalCloseToUrl(id, options);

      // Clear modal data when closing
      dataStore.clearModalData(id);

      // Close modal in stack as fallback (in case URL sync doesn't work)
      modalStack.close(id);
    },
    [] // Empty dependency array - callbacks are stable
  );

  const closeAllModals = React.useCallback(() => {
    const { dataStore, modalStack, urlSync } = configRef.current;
    
    modalStack.closeAll();

    // Clear all modal data
    dataStore.clearAllModalData();

    // Sync to URL if enabled
    urlSync.syncAllModalsClosedToUrl();
  }, []); // Empty dependency array - callbacks are stable

  return {
    openModal,
    closeModal,
    closeAllModals,
  };
};
